import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  getPresignedUrlExpiresAtMs,
  presignedResourceKey,
} from "./presignedUrl";

/** How often a held link that died mid-playback is reconsidered. */
const RECHECK_WHILE_PLAYING_MS = 5_000;

/**
 * A media element keeps the link it loaded on until that link fails, then
 * takes the newest one: a renewed signature for the same bytes never makes
 * the browser download them again, and a dead link is replaced as soon as
 * a live one exists. The canvas video card has behaved this way for a while
 * (FileRecordThumbnail); this is that pattern for the viewers.
 *
 * A caller that attaches the `ref` also has its held link swapped once that
 * link expires, while nothing is playing: a paused element that reaches for
 * a dead link stalls for half a minute before the browser gives up and fires
 * `error`, so the press that started it does nothing. Playback in progress is
 * left alone — it runs out of the buffer it already has, and `onError` still
 * covers it if that buffer runs dry.
 *
 * Returns the `src` to render, a `ref` for the element, and an `onError`.
 * `onError` answers true when it swapped to a newer link (the caller keeps
 * waiting) and false when there is nothing newer (the caller shows its
 * failure). A swap reloads the element, so the position it was at is kept
 * and seeked back to on the next `loadedmetadata`.
 */
export function useStickyMediaSrc<T extends HTMLMediaElement>(
  latestUrl: string | undefined,
) {
  const [src, setSrc] = useState(latestUrl);
  const srcRef = useRef(latestUrl);
  const latestRef = useRef(latestUrl);
  latestRef.current = latestUrl;
  const failedRef = useRef<string | undefined>(undefined);
  const ref = useRef<T | null>(null);
  const resumeAtRef = useRef(0);

  const take = (next: string | undefined) => {
    srcRef.current = next;
    failedRef.current = undefined;
    setSrc(next);
  };

  const keepPosition = () => {
    const el = ref.current;
    if (el && Number.isFinite(el.currentTime))
      resumeAtRef.current = el.currentTime;
  };

  useLayoutEffect(() => {
    const current = srcRef.current;
    if (latestUrl === current) return;
    // A different resource (new version, or nothing at all) always wins;
    // the same resource re-signed only replaces a link that has failed or
    // has run out of life.
    const sameResource =
      !!latestUrl &&
      !!current &&
      presignedResourceKey(latestUrl) === presignedResourceKey(current);
    if (!sameResource || failedRef.current === current) {
      take(latestUrl);
      return;
    }

    // Only a media element stalls on a dead link. The callers that render an
    // `<img>` or an `<iframe>` attach no ref, and a swap would cost them a
    // re-download or bounce a PDF reader back to page one.
    const diesAt = ref.current ? getPresignedUrlExpiresAtMs(current) : null;
    if (diesAt === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const swapWhenIdle = () => {
      const el = ref.current;
      if (el && !el.paused && !el.ended) {
        timer = setTimeout(swapWhenIdle, RECHECK_WHILE_PLAYING_MS);
        return;
      }
      keepPosition();
      take(latestRef.current);
    };
    const life = diesAt - Date.now();
    if (life <= 0) {
      swapWhenIdle();
    } else {
      timer = setTimeout(swapWhenIdle, life);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [latestUrl]);

  const onError = useCallback((): boolean => {
    const current = srcRef.current;
    const latest = latestRef.current;
    if (latest && latest !== current) {
      keepPosition();
      take(latest);
      return true;
    }
    failedRef.current = current;
    return false;
  }, []);

  /** Where a swap interrupted playback, once; 0 when it did not. */
  const takeResumeTime = useCallback(() => {
    const at = resumeAtRef.current;
    resumeAtRef.current = 0;
    return at;
  }, []);

  const onLoadedMetadata = useCallback(() => {
    const el = ref.current;
    const at = takeResumeTime();
    if (el && at > 0) el.currentTime = at;
  }, [takeResumeTime]);

  return { src, onError, ref, takeResumeTime, onLoadedMetadata };
}
